import { MAPPED_ROAD_VEHICLE_STREAMING } from "../config/vehicles.ts";

export interface MappedRoadVehicleActivationCandidate {
  readonly id: string;
  readonly position: Readonly<{ x: number; z: number }>;
}

export interface MappedRoadVehicleActivationInput {
  readonly playerPosition: Readonly<{ x: number; z: number }>;
  readonly sourceVehicles: readonly Readonly<
    MappedRoadVehicleActivationCandidate
  >[];
  readonly retainedVehicles: readonly Readonly<
    MappedRoadVehicleActivationCandidate
  >[];
  readonly mountedVehicleId?: string;
}

export interface MappedRoadVehicleVisualTiers {
  /** Active retained cars require their moved, simulation-owned presentation. */
  readonly individualVehicleIds: ReadonlySet<string>;
  /** Every retained car suppresses its immutable source proxy, even off-screen. */
  readonly suppressedSourceVehicleIds: ReadonlySet<string>;
}

/**
 * Selects the bounded full-simulation tier. A mounted mapped car always wins,
 * then nearby previously used cars, then untouched source cars by distance.
 */
export function selectMappedRoadVehicleActivation(
  input: Readonly<MappedRoadVehicleActivationInput>,
): ReadonlySet<string> {
  const selected = new Set<string>();
  const retainedIds = new Set(input.retainedVehicles.map((vehicle) =>
    vehicle.id
  ));
  if (input.mountedVehicleId !== undefined) {
    selected.add(input.mountedVehicleId);
  }
  addNearestWithinRadius(
    selected,
    input.retainedVehicles,
    input.playerPosition,
    MAPPED_ROAD_VEHICLE_STREAMING.retainedActivationRadiusM,
  );
  addNearestWithinRadius(
    selected,
    input.sourceVehicles.filter((vehicle) => !retainedIds.has(vehicle.id)),
    input.playerPosition,
    MAPPED_ROAD_VEHICLE_STREAMING.interactiveActivationRadiusM,
  );
  return selected;
}

/**
 * Keeps untouched active cars in the four-draw-call instanced field. A source
 * car is promoted to an individual render only after the player has used it;
 * its original proxy then stays suppressed so it cannot reappear at the
 * vacated parking space after streaming churn.
 */
export function selectMappedRoadVehicleVisualTiers(
  activeVehicleIds: ReadonlySet<string>,
  retainedVehicleIds: ReadonlySet<string>,
): Readonly<MappedRoadVehicleVisualTiers> {
  return {
    individualVehicleIds: new Set(
      [...retainedVehicleIds].filter((id) => activeVehicleIds.has(id)),
    ),
    suppressedSourceVehicleIds: new Set(retainedVehicleIds),
  };
}

function addNearestWithinRadius(
  selected: Set<string>,
  candidates: readonly Readonly<MappedRoadVehicleActivationCandidate>[],
  playerPosition: Readonly<{ x: number; z: number }>,
  radiusM: number,
): void {
  const remaining = MAPPED_ROAD_VEHICLE_STREAMING.maximumNearbyPhysicsBodies -
    selected.size;
  if (remaining <= 0) return;
  const nearest = candidates.flatMap((candidate) => {
    if (selected.has(candidate.id)) return [];
    const distanceM = Math.hypot(
      candidate.position.x - playerPosition.x,
      candidate.position.z - playerPosition.z,
    );
    return distanceM <= radiusM ? [{ ...candidate, distanceM }] : [];
  }).sort((left, right) =>
    left.distanceM - right.distanceM || left.id.localeCompare(right.id)
  ).slice(0, remaining);
  for (const candidate of nearest) selected.add(candidate.id);
}
